import { useNavigate, useParams } from "@tanstack/react-router";
import { Alert, Button, DataTable, type ColumnDef, SectionCard } from "@/deps/ui-library";
import {
  useDiscardSupplierPayment,
  usePostSupplierPayment,
  useSupplierPayment,
} from "@/api/queries";
import type { SupplierPayment } from "@/api/types";
import { useCan } from "@/app/session";
import {
  ApprovalActions,
  ApprovalCard,
  useApprovalHoldsDraft,
} from "@/components/DocumentApproval";
import { DocumentScreen } from "@/components/DocumentScreen";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";

type Application = SupplierPayment["applications"][number];

const APPLIED_COLUMNS: Array<ColumnDef<Application>> = [
  {
    id: "bill",
    header: "Bill",
    cell: ({ row: { original: line } }) => <span className="num">{line.billNumber}</span>,
  },
  {
    id: "applied",
    header: "Applied",
    meta: { textAlign: "right" },
    cell: ({ row: { original: line } }) => <span className="num">{formatAmount(line.amount)}</span>,
  },
];

export function PaymentDetailPage() {
  const { id } = useParams({ from: "/purchases/payments/$id" });
  const navigate = useNavigate();
  const { data, isPending, error } = useSupplierPayment(id);
  const post = usePostSupplierPayment();
  const discard = useDiscardSupplierPayment();
  const can = useCan();
  const draft = data?.status === "draft";
  const held = useApprovalHoldsDraft("payment", id, draft);

  if (isPending) return <p className="text-fg-muted p-6 text-sm">Loading payment…</p>;
  if (error || !data)
    return <p className="text-bad-text p-6 text-sm">That payment could not be loaded.</p>;

  const withheld = Number(data.withheld) > 0;
  const failure = post.error ?? discard.error;

  return (
    <DocumentScreen
      title="Payment"
      number={data.number}
      status={draft ? "draft" : "approved"}
      locked={!draft}
      subtitle={`${data.supplierName} · ${formatDate(data.date)}`}
      actions={
        draft ? (
          <span className="flex items-center gap-2">
            {!held && can("purchases.payments.create") ? (
              <Button
                colorScheme="default"
                variant="ghost"
                size="sm"
                disabled={discard.isPending}
                onClick={() =>
                  discard.mutate(id, {
                    onSuccess: () => void navigate({ to: "/purchases/payments" }),
                  })
                }
              >
                Discard
              </Button>
            ) : null}
            <ApprovalActions
              kind="payment"
              id={id}
              canCreate={can("purchases.payments.create")}
              canIssue={can("purchases.payments.post")}
              issuing={post.isPending}
              onIssue={() => post.mutate(id)}
              issueLabel="Post"
              approveLabel="Approve and post"
              workingLabel="Posting"
            />
          </span>
        ) : null
      }
      facts={[
        { label: "Paid to", value: data.supplierName },
        { label: "Date", value: formatDate(data.date) },
        {
          label: "Method",
          value: data.reference ? `${data.method} ${data.reference}` : data.method,
        },
        { label: "Paid from", value: data.paidFromName },
        {
          label: draft ? "Net to pay" : "Net paid",
          value: <span className="num">{formatAmount(data.net)}</span>,
        },
      ]}
      history={data.history}
    >
      {failure ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(failure as Error).message}
        />
      ) : null}

      {draft ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "info" }}
          description="This payment hasn't been posted. It settles nothing and isn't in the ledger until it is."
        />
      ) : null}

      <ApprovalCard kind="payment" id={id} />

      <SectionCard title="Applied to" flush>
        {data.applications.length === 0 ? (
          <p className="text-fg-muted p-4 text-sm">
            {draft
              ? "This payment names no bill. Once posted, the whole amount is held as a supplier advance."
              : "No bill has been settled by this payment. The whole amount is held as a supplier advance."}
          </p>
        ) : (
          <DataTable
            aria-label="Bills settled by this payment"
            size="sm"
            enableSorting={false}
            columns={APPLIED_COLUMNS}
            data={data.applications}
            getRowId={(line) => line.billId}
            pagination={{ mode: "manual", show: false }}
          />
        )}
      </SectionCard>

      <SectionCard title="Amounts">
        <dl className="flex flex-col gap-2 text-sm">
          <div className="flex items-baseline justify-between">
            <dt className="text-fg-muted">Gross amount</dt>
            <dd className="num text-fg">{formatAmount(data.gross)}</dd>
          </div>
          {withheld ? (
            <div className="flex items-baseline justify-between">
              <dt className="text-fg-muted">
                Less: tax withheld · {data.withheldCode} at {data.withheldRate}%
              </dt>
              <dd className="num text-fg">{formatAmount(data.withheld)}</dd>
            </div>
          ) : null}
          {Number(data.advance) > 0 ? (
            <div className="flex items-baseline justify-between">
              <dt className="text-fg-muted">
                {draft ? "Will be held as a supplier advance" : "Still held as a supplier advance"}
              </dt>
              <dd className="num text-fg">{formatAmount(data.advance)}</dd>
            </div>
          ) : null}
          <div className="border-line-soft flex items-baseline justify-between border-t pt-2 font-semibold">
            <dt className="text-fg">
              {draft ? "Net to pay" : "Net paid"} · {data.currency}
            </dt>
            <dd className="num text-fg">{formatAmount(data.net)}</dd>
          </div>
        </dl>
      </SectionCard>
    </DocumentScreen>
  );
}
